"use client"

import { convertToLocale } from "@lib/util/money"
import React from "react"
import { HttpTypes } from "@medusajs/types"

type CartTotalsProps = {
  totals: {
    total?: number | null
    subtotal?: number | null
    tax_total?: number | null
    currency_code: string
    item_subtotal?: number | null
    shipping_subtotal?: number | null
    discount_subtotal?: number | null
    original_item_total?: number | null
    item_total?: number | null
  }
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
}

const computeFreeShippingTarget = (
  totals: CartTotalsProps["totals"],
  shippingOptions: HttpTypes.StoreCartShippingOption[]
) => {
  const currentAmount =
    totals.original_item_total ??
    totals.subtotal ??
    totals.item_total ??
    totals.item_subtotal ??
    0

  const freeShippingPrice = shippingOptions
    .map((shippingOption) => {
      const validCurrencyPrices = shippingOption.prices.filter(
        (price) =>
          price.currency_code === totals.currency_code &&
          (price.price_rules || []).some(
            (priceRule) => priceRule.attribute === "item_total"
          )
      )

      return validCurrencyPrices.map((price) => {
        const priceRule = (price.price_rules || []).find(
          (pr) => pr.attribute === "item_total"
        )

        if (!priceRule) return null

        const targetAmount = parseFloat(priceRule.value)

        if (Number.isNaN(targetAmount)) return null

        let targetReached = false

        if (priceRule.operator === "gt") {
          targetReached = currentAmount > targetAmount
        } else if (priceRule.operator === "gte") {
          targetReached = currentAmount >= targetAmount
        } else if (priceRule.operator === "lt") {
          targetReached = targetAmount > currentAmount
        } else if (priceRule.operator === "lte") {
          targetReached = targetAmount >= currentAmount
        } else {
          targetReached = currentAmount === targetAmount
        }

        return {
          amount: price.amount,
          target_amount: targetAmount,
          target_reached: targetReached,
        }
      })
    })
    .flat(1)
    .filter(Boolean)
    .find((price) => price?.amount === 0)

  return {
    freeShippingReached: freeShippingPrice?.target_reached ?? false,
  }
}

const CartTotals: React.FC<CartTotalsProps> = ({
  totals,
  shippingOptions = [],
}) => {
  const {
    currency_code,
    total,
    tax_total,
    item_subtotal,
    shipping_subtotal,
    discount_subtotal,
  } = totals

  const { freeShippingReached } = computeFreeShippingTarget(
    totals,
    shippingOptions
  )

  const firstPaidShippingOption = shippingOptions.find((option) => {
    const amount = option.calculated_price?.calculated_amount ?? 0
    return amount > 0
  })

  const fallbackShippingAmount =
    firstPaidShippingOption?.calculated_price?.calculated_amount ?? 0

  const shippingAmount = freeShippingReached
    ? 0
    : (shipping_subtotal ?? 0) > 0
      ? shipping_subtotal ?? 0
      : fallbackShippingAmount

  const displayTotal = freeShippingReached
    ? total ?? 0
    : (shipping_subtotal ?? 0) > 0
      ? total ?? 0
      : (total ?? 0) + shippingAmount

  return (
    <div>
      <div className="flex flex-col gap-y-2 txt-medium text-ui-fg-subtle">
        <div className="flex items-center justify-between">
          <span>Subtotal (excl. shipping and taxes)</span>
          <span data-testid="cart-subtotal" data-value={item_subtotal || 0}>
            {convertToLocale({ amount: item_subtotal ?? 0, currency_code })}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span>Shipping</span>
          <span data-testid="cart-shipping" data-value={shippingAmount}>
            {freeShippingReached
              ? "FREE"
              : convertToLocale({
                  amount: shippingAmount,
                  currency_code,
                })}
          </span>
        </div>

        {!!discount_subtotal && (
          <div className="flex items-center justify-between">
            <span>Discount</span>
            <span
              className="text-ui-fg-interactive"
              data-testid="cart-discount"
              data-value={discount_subtotal || 0}
            >
              -{" "}
              {convertToLocale({
                amount: discount_subtotal ?? 0,
                currency_code,
              })}
            </span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="flex gap-x-1 items-center">Taxes</span>
          <span data-testid="cart-taxes" data-value={tax_total || 0}>
            {convertToLocale({ amount: tax_total ?? 0, currency_code })}
          </span>
        </div>
      </div>

      <div className="h-px w-full border-b border-gray-200 my-4" />

      <div className="flex items-center justify-between text-ui-fg-base mb-2 txt-medium">
        <span>Total</span>
        <span
          className="txt-xlarge-plus"
          data-testid="cart-total"
          data-value={displayTotal}
        >
          {convertToLocale({ amount: displayTotal, currency_code })}
        </span>
      </div>

      <div className="h-px w-full border-b border-gray-200 mt-4" />
    </div>
  )
}

export default CartTotals